import CurrencyIcon from "components/shared/CurrencyIcon";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import StyledButton from "components/shared/StyledButton";
import DropZoneCSV from "components/shared/UploadCsv";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { uploadFile } from "store/documentStore";
import { useAppDispatch } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { ETransferTermSalesStatus, updateTransferTermSale } from "store/transferTermSalesStore";

import styles from "./style.module.scss";
interface IHandleRightsModal {
    /**
     * Term sale being modified.
     */
    termSale: ITransferTermSale | undefined;
    /**
     * Triggers closing modal.
     */
    handleClose: ({
        updatedSale,
        updatedSales,
    }: {
        updatedSale?: ITransferTermSale;
        updatedSales?: ITransferTermSale[];
    }) => void;
    /**
     * Is it on new or edit mode?
     */
    formMode: string;
    currentSales: ITransferTermSale[];
}

// Modal from TransferTermSalesTable which allows user to add grossAmount/netAmount as well as statement PDF.
const HandleRightsModal = ({ termSale, handleClose, formMode, currentSales }: IHandleRightsModal) => {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();

    const [files, setFiles] = useState<File[]>();
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [submitPending, setSubmitPending] = useState(false);

    const defaultValues = {
        grossAmount: formMode === "edit" ? (termSale?.grossAmount ?? 0) / 100 : 0,
        netAmount: formMode === "edit" ? (termSale?.netAmount ?? 0) / 100 : 0,
        statementUrl: "",
    };

    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
    } = useForm({ defaultValues, mode: "all" });

    const onSubmit = async (data: any) => {
        setSubmitPending(true);
        if (termSale) {
            const updatedSale = {
                grossAmount: Math.round(+(data.grossAmount ?? 0) * 100),
                netAmount: Math.round(+(data.netAmount ?? 0) * 100),
                statementUrl: data.statementUrl,
                status: termSale.type === "advance" ? termSale.status : ETransferTermSalesStatus.INVOICE_TO_ISSUE,
            };

            const response = await dispatch(updateTransferTermSale({ saleId: termSale.id, data: updatedSale }));
            if (response.meta.requestStatus === "fulfilled") {
                setSubmitPending(false);

                const newSales = currentSales.map((sale) => {
                    if (sale.id === termSale.id) {
                        return { ...termSale, ...updatedSale };
                    }
                    return sale;
                });
                handleClose({ updatedSales: newSales });
            } else {
                setSubmitPending(false);
            }
        }
    };

    // Save url into statementUrl property once file chosen.
    useEffect(() => {
        if (files && files.length > 0) {
            setIsFileLoading(true);
            const file: File = files[0];
            const fileName = file.name ? file.name : "";
            const formData = new FormData();
            formData.append("file", file, fileName);

            dispatch(uploadFile({ formData })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setValue(`statementUrl`, resp.payload.imagePath, { shouldDirty: true });
                    setIsFileLoading(false);
                }
            });
        }
    }, [files]);

    return (
        <form className={styles["rights-form-container"]} onSubmit={handleSubmit(onSubmit)}>
            <NumberFormComponent
                control={control}
                controlName="grossAmount"
                label={t("TransferTerms.TermTable.RoyaltiesCreated")}
                icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                error={errors.grossAmount}
                required
                float
            />
            <NumberFormComponent
                control={control}
                controlName="netAmount"
                label={t("TransferTerms.TermTable.RoyaltiesPaid")}
                icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                error={errors.netAmount}
                required
                float
            />
            <div>
                <span className={styles["dropzone-info"]}>
                    {formMode === "new"
                        ? t("TransferTerms.HandleRightsModal.AddStatement")
                        : t("TransferTerms.HandleRightsModal.UpdateStatement")}
                </span>
                <DropZoneCSV setFile={setFiles} file={files} acceptedFilesTypeList={{ "application/pdf": [".pdf"] }} />
            </div>
            {termSale?.salesId && (
                <span className={styles["info-transfer"]}>
                    {t("TransferTerms.HandleRightsModal.AlreadyTransferred")}
                </span>
            )}
            <StyledButton buttonType="primary" type="submit" disabled={isFileLoading} loading={submitPending}>
                {t("TransferTerms.HandleRightsModal.SaveRights")}
            </StyledButton>
        </form>
    );
};

export default HandleRightsModal;
